import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { User } from '@shared/models';
import { cloneDeep } from 'lodash-es';
import { ClientWithAssignments, GridDrawerComponent } from '../../models';
import { ClientService } from '../../services';

@Component({
  standalone: false,
  selector: 'gstar-assign-client',
  templateUrl: 'assign-client.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignClientComponent
  extends GridDrawerComponent<ClientWithAssignments>
  implements OnInit
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly clientService = inject(ClientService);

  enableUpdate: boolean = false;

  public form: FormGroup = this.formBuilder.group({
    clientAssignments: this.formBuilder.array([]),
  });

  get clientAssignments(): FormArray<FormControl<User | null>> {
    return this.form.get('clientAssignments') as FormArray;
  }

  prevModel!: ClientWithAssignments;

  destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values: ClientWithAssignments) => {
        if (this.isEdit) this.checkChanges(values);
        this.modelChange.emit({
          model: {
            ...this.model,
            ...values,
          },
          prevModel: this.prevModel!,
          isValid:
            this.isEdit && this.form.valid
              ? this.enableUpdate
              : this.form.valid,
        });
      });
    if (!this.model) return;

    this.clientService
      .getClientAssignment(this.model.clientId)
      .subscribe(clientAssignments => {
        this.initForm(clientAssignments);
      });
  }

  initForm(clientAssignments: User[]): void {
    clientAssignments.forEach(clientAssignment => {
      const newFormControl = this.formBuilder.control(clientAssignment);
      this.clientAssignments.push(newFormControl);
    });

    this.prevModel = cloneDeep({
      ...this.model!,
      clientAssignments,
    });
  }

  userSelect(user: User): void {
    if (this.isUserAlreadyAdded(user)) return;
    const newFormControl = this.formBuilder.control(user);
    this.clientAssignments.push(newFormControl);
    this.sortclientAssignments();
  }

  removeAssignment(index: number): void {
    this.clientAssignments.removeAt(index);
  }

  isUserAlreadyAdded(user: User): boolean {
    return this.clientAssignments.value.some(
      (addedUser: User | null) => addedUser?.id === user.id
    );
  }

  private sortclientAssignments(): void {
    this.clientAssignments.controls.sort(
      (a: FormControl<User | null>, b: FormControl<User | null>) => {
        const nameA = (a.value?.firstName || '').toUpperCase();
        const nameB = (b.value?.firstName || '').toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      }
    );
  }

  private checkChanges(values: ClientWithAssignments): void {
    let hasUserChanged: boolean = false;

    if (values && this.prevModel) {
      if (
        values.clientAssignments.length !==
        this.prevModel.clientAssignments.length
      ) {
        this.enableUpdate = true;
      } else {
        values.clientAssignments.forEach((user: User) => {
          if (
            !this.prevModel.clientAssignments.some(
              (u: User) => u.id === user.id
            )
          ) {
            hasUserChanged = true;
          }
        });
        this.enableUpdate = hasUserChanged;
      }
    }
  }
}
